/**
 * Context-locked palette for the "Boil Water Advisories" layer (#213, Advisories &
 * Declarations group). Manitoba public-water-system boil-water advisories from the
 * arcgis_public_features `public_health` category — POINT features (one per water
 * system), severity the upstream `Timeframe_to_Resolve` (e.g. "Long-term").
 *
 * The register is a WATER public-health advisory, so the family owns a deep CYAN
 * identity — water-coded, but a darker, bluer cyan than the bright aircraft teal so
 * the two never read as the same thing. Distinct from the urgent red (Colour Law 5)
 * and the fire/flood/evac families. A long-standing advisory reads heavier (deeper
 * cyan, larger dot) than a short-term one. Colour is always paired with the label.
 *
 * This module must NOT import another palette family (ESLint no-restricted-imports).
 */
import type { ArcgisLayerStyle } from '../layers/arcgisStyle';

const LONG_TERM: ArcgisLayerStyle = {
  color: '#0e7490', // cyan-700 — the persistent, heavier register
  fillAlpha: 0.16,
  strokeAlpha: 0.92,
  outlineWidth: 3,
  pointPixelSize: 13,
  label: 'Boil water (long-term)',
};

const SHORT_TERM: ArcgisLayerStyle = {
  color: '#0891b2', // cyan-600 — the lighter, shorter-duration register
  fillAlpha: 0.14,
  strokeAlpha: 0.88,
  outlineWidth: 2,
  pointPixelSize: 11,
  label: 'Boil water (short-term)',
};

const UNKNOWN: ArcgisLayerStyle = {
  color: '#22a6c3', // a neutral mid cyan — an unmapped timeframe still renders
  fillAlpha: 0.12,
  strokeAlpha: 0.85,
  outlineWidth: 2,
  pointPixelSize: 11,
  label: 'Boil water advisory',
};

/**
 * Resolve the style for a boil-water `Timeframe_to_Resolve`. "Long-term" / "long
 * standing" → the heavier register; "short" / "temporary" → the lighter one;
 * null/other → the neutral mid cyan (still rendered — never dropped).
 */
export function publicHealthStyle(severity: string | null | undefined): ArcgisLayerStyle {
  const s = (severity ?? '').toLowerCase();
  if (s.includes('long')) return LONG_TERM;
  if (s.includes('short') || s.includes('temp')) return SHORT_TERM;
  return UNKNOWN;
}
